import React from 'react';

import { Document } from '@contentful/rich-text-types';
import { Box, Col, getCSSVariable, Link, Row, Text } from '@opendoor/bricks-next';
import { Image } from '@opendoor/bricks/core';

import LazyLoad from 'components/helpers/LazyLoad';

import { ILpElementSimpleList } from 'declarations/contentful';

import { useT } from 'helpers/i18n';

import {
  BNColorThemes,
  getBNComponentThemeColors,
  IBackgroundColors,
  IBNComponentThemeConfig,
} from '../styles/ComponentTheme';
import FooterAppDownload from './FooterAppDownload';
import FooterBrand from './FooterBrand';
import FooterHelpCenter from './FooterHelpCenter';
import FooterLegal from './FooterLegal';
import FooterNav from './FooterNav';

export interface IFooterProps {
  backgroundColor?: IBackgroundColors;
  footerImage?: string;
  footerImageTitle?: string;
  footerNav?: ILpElementSimpleList[];
  footerTitle?: string;
  sellInYourMarket?: { label: string; path: string }[];
  googlePlaystoreLink?: string;
  appleStoreLink?: string;
  helpCenterLink?: string;
  extraLegalText?: Document | undefined;
  showCustomerTestimonialLegal?: boolean | undefined;
  showEligibilityLegal?: boolean | undefined;
  showPromotionLegal?: boolean | undefined;
  showThePdpCopyrightLegal?: boolean | undefined;
  locale?: string;
}

const FooterSellInYourMarket = ({
  sellInYourMarket,
  theme,
}: {
  sellInYourMarket: IFooterProps['sellInYourMarket'];
  theme: IBNComponentThemeConfig;
}) => {
  const { t } = useT();

  if (!sellInYourMarket?.length) {
    return null;
  }
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      width="100%"
      padding="$5x"
      backgroundColor={theme.bgPrimaryLight}
      $smallerThanMD={{
        flexDirection: 'column',
      }}
      borderRadius="$6x"
      gap="$4x"
      overflow="hidden"
    >
      <Text
        tag="p"
        minWidth="$80x"
        flexShrink={0}
        typography="$labelSmall"
        color={theme.contentPrimary}
      >
        {t('homepage.links.sell-in-your-market', 'Learn about selling in your market')}
      </Text>
      <Row
        gap="$4x"
        flexWrap="wrap"
        alignItems="center"
        flex={1}
        minWidth={0}
        $smallerThanMD={{
          justifyContent: 'center',
        }}
      >
        {sellInYourMarket.map((market, i) => (
          <React.Fragment key={market.path}>
            <Box tag="li">
              <Link
                analyticsName={`footer-link-column-${market.label}`}
                typography="$bodyMedium"
                href={`/sell/${market.path}`}
                variant="secondaryInverse"
                textDecorationLine="none"
              >
                {market.label}
              </Link>
            </Box>
            {i < sellInYourMarket.length - 1 && <Box tag="span">|</Box>}
          </React.Fragment>
        ))}
      </Row>
    </Box>
  );
};

export const FooterForegroundBox = ({
  children,
  title,
  theme,
}: {
  children: React.ReactNode;
  title: string;
  theme: IBNComponentThemeConfig;
  isAppButtonFooter?: boolean;
}) => {
  return (
    <Box
      flex={1}
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      $largerThanSM={{ flexDirection: 'row', justifyContent: 'space-between' }}
      width="100%"
      padding="$6x"
      gap="$4x"
      backgroundColor={theme.bgPrimaryLight}
      borderRadius={getCSSVariable('$6x', 'radius')}
    >
      <Text tag="p" flexShrink={0} typography="$labelSmall" color={theme.contentPrimary}>
        {title}
      </Text>
      {children}
    </Box>
  );
};

const Footer = ({
  backgroundColor = BNColorThemes.Grey,
  footerNav,
  footerTitle,
  sellInYourMarket,
  googlePlaystoreLink,
  appleStoreLink,
  helpCenterLink,
  extraLegalText,
  showCustomerTestimonialLegal,
  showEligibilityLegal,
  showPromotionLegal,
  showThePdpCopyrightLegal,
  locale,
}: IFooterProps) => {
  const theme = getBNComponentThemeColors(
    backgroundColor in BNColorThemes ? (backgroundColor as BNColorThemes) : BNColorThemes.Grey,
  );
  const appleStoreImageUrl =
    locale === 'es'
      ? 'https://imgdrop.imgix.net/2025-06-1750894044413-61865.png?preset=square-2048'
      : 'https://imgdrop.imgix.net/2023-12-1702502078078-95093.png?preset=square-2048';
  const googlePlayStoreImageUrl =
    locale === 'es'
      ? 'https://imgdrop.imgix.net/2025-06-1750894682317-95550.png?preset=square-2048'
      : 'https://imgdrop.imgix.net/2023-12-1702502078081-28903.png?preset=square-2048';

  return (
    <Col
      tag="footer"
      backgroundColor={theme.bgPrimary}
      // @ts-expect-error theme token type mismatch with getCSSVariable
      style={{ color: getCSSVariable(theme.contentPrimary) }}
      minHeight={450}
    >
      <LazyLoad>
        <Box>
          <Box
            display="block"
            maxWidth={1440}
            marginHorizontal="auto"
            padding="$10x"
            paddingTop="$44x"
            $largerThanSM={{ padding: '$40x' }}
            width="100%"
          >
            <Box
              gap="$32x"
              maxWidth="100vw"
              $largerThanMD={{ flexDirection: 'row' }}
              flexDirection="column"
            >
              <FooterBrand footerTitle={footerTitle} theme={theme} />
              <Col gap="$8x" flex={1}>
                <FooterNav footerNav={footerNav} theme={theme} />
                <FooterSellInYourMarket sellInYourMarket={sellInYourMarket} theme={theme} />
                <Box $largerThanLG={{ flexDirection: 'row' }} gap="$8x">
                  <FooterAppDownload
                    theme={theme}
                    appleStoreLink={appleStoreLink}
                    appleStoreImageUrl={appleStoreImageUrl}
                    googlePlaystoreLink={googlePlaystoreLink}
                    googlePlayStoreImageUrl={googlePlayStoreImageUrl}
                    locale={locale}
                  />
                  <FooterHelpCenter theme={theme} helpCenterLink={helpCenterLink} />
                </Box>

                <FooterLegal
                  theme={theme}
                  extraLegalText={extraLegalText}
                  showCustomerTestimonialLegal={showCustomerTestimonialLegal}
                  showEligibilityLegal={showEligibilityLegal}
                  showPromotionLegal={showPromotionLegal}
                  showThePdpCopyrightLegal={showThePdpCopyrightLegal}
                />

                {/* Copyright + year section */}
                <Row pt="$32x" alignItems="center" gap="$8x" flexWrap="wrap">
                  <Text tag="span" typography="$bodyMedium" color={theme.contentPrimary}>
                    &copy; {new Date().getFullYear()} Opendoor | All rights reserved.
                  </Text>
                  <Row
                    gap="$8x"
                    alignItems="center"
                    // @ts-expect-error theme token type mismatch with getCSSVariable
                    style={{ color: getCSSVariable(theme.contentPrimary) }}
                  >
                    <Image
                      src="https://images.opendoor.com/source/s3/imgdrop-production/f2398a1e83a24962a9297d13f2ac6ea5.png?preset=square-2048"
                      loading="lazy"
                      alt="Equal Housing Opportunity"
                      width={38}
                      height={38}
                    />
                    <Link
                      href="https://www.bbb.org/us/az/tempe/profile/real-estate-services/opendoor-1126-1000060421/#sealclick"
                      analyticsName="cosmos-landing-page-v2-footer-bbb-link"
                      aria-label="Better Business Bureau accredited since June 8th 2021 link"
                      hoverStyle={{ color: theme.contentPrimary }}
                      color={theme.contentPrimary}
                      textDecorationLine="underline"
                    >
                      <Image
                        src="https://images.opendoor.com/source/s3/imgdrop-production/1cd65eee0b8345689ceff0292986f125.png?preset=square-2048"
                        loading="lazy"
                        alt="Better Business Bureau"
                        height={38}
                      />
                    </Link>
                  </Row>
                </Row>
              </Col>
            </Box>
          </Box>
        </Box>
      </LazyLoad>
    </Col>
  );
};

export default Footer;
